// Bean portrait thumbnails (awards, locker, bean card, name prompt) rendered by the renderer's
// locker stage. A portrait is a synchronous GPU render plus a readback, so each look is drawn once
// and copied for every use; the cache is a small LRU.
import type { Cosmetics } from '@shared/cosmetics';

const MAX_ENTRIES = 48;

/** True when two cosmetic sets are equal field by field. */
export function sameCosmetics(a: Cosmetics, b: Cosmetics): boolean {
  return a.color === b.color && a.pattern === b.pattern && a.hat === b.hat && a.eyes === b.eyes && a.honk === b.honk
    && a.trail === b.trail && a.scream === b.scream && a.celebration === b.celebration && a.identityPref === b.identityPref;
}

/** The visual key of a look (honk, scream and celebration do not change the picture). */
export function lookKey(c: Cosmetics, identity: number, size: number): string {
  return `${c.color}.${c.pattern}.${c.hat}.${c.eyes}.${c.trail}:${identity}@${size}`;
}

export class PortraitCache {
  private readonly cache = new Map<string, HTMLCanvasElement>();
  private failed = false;

  constructor(private readonly render: (c: Cosmetics, identity: number, size: number) => HTMLCanvasElement) {}

  /** A fresh canvas showing the look (copies are cheap; callers may mount it anywhere). */
  get(c: Cosmetics, identity: number, size: number): HTMLCanvasElement | null {
    if (this.failed) return null;
    const key = lookKey(c, identity, size);
    let src = this.cache.get(key);
    if (!src) {
      try {
        src = this.render(c, identity, size);
      } catch {
        this.failed = true;
        return null;
      }
      this.cache.set(key, src);
      if (this.cache.size > MAX_ENTRIES) {
        const oldest = this.cache.keys().next().value;
        if (oldest !== undefined) this.cache.delete(oldest);
      }
    } else {
      // Refresh recency.
      this.cache.delete(key);
      this.cache.set(key, src);
    }
    const copy = document.createElement('canvas');
    copy.width = src.width;
    copy.height = src.height;
    copy.className = 'portrait';
    copy.getContext('2d')?.drawImage(src, 0, 0);
    return copy;
  }
}
